import React from "react";
import { Button, ButtonProps, IconButton, IconButtonProps } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";

type ThemeValue = string | ((theme: any) => string);

/* -------------------------------------------------------------------------- */
/*                               RoundIconButton                              */
/* -------------------------------------------------------------------------- */

export type RoundIconButtonColour = "coloured" | "clear";
export type RoundIconButtonSize = "separate" | "inline";

export interface RoundIconButtonProps
  extends Omit<IconButtonProps, "size" | "color"> {
  /**
   * coloured = filled circular button
   * clear = transparent circular button
   */
  colour?: RoundIconButtonColour;

  /**
   * separate = larger standalone button, e.g. record selector add button
   * inline = smaller inline button, e.g. profile page buttons
   */
  displaySize?: RoundIconButtonSize;

  /**
   * MUI palette key, e.g. "primary", "secondary".
   */
  colorKey?: string;

  /**
   * Direct colour override.
   * Useful when parent already calculated a route/theme colour.
   */
  primaryColor?: string;

  /**
   * Icon to render.
   * Defaults to AddIcon.
   */
  icon?: React.ReactNode;

  /**
   * Icon colour.
   * Can be a string or theme callback.
   */
  iconColor?: ThemeValue;

  /**
   * Icon colour on hover.
   * Can be a string or theme callback.
   */
  hoverIconColor?: ThemeValue;

  /**
   * Background colour on hover.
   * Can be a string or theme callback.
   */
  hoverBackgroundColor?: ThemeValue;

  /**
   * Allows use as anchor link:
   * component="a"
   * href="..."
   */
  component?: React.ElementType;

  href?: string;
  target?: string;
  rel?: string;
}

export const RoundIconButton: React.FC<RoundIconButtonProps> = ({
  colour = "clear",
  displaySize = "inline",
  colorKey = "primary",
  primaryColor: primaryColorProp,
  icon = <AddIcon fontSize="small" />,
  iconColor,
  hoverIconColor,
  hoverBackgroundColor,
  sx,
  ...props
}) => {
  const theme = useTheme();

  const paletteEntry = theme.palette[colorKey as keyof typeof theme.palette];

  const palettePrimaryColor =
    typeof paletteEntry === "object" && paletteEntry && "main" in paletteEntry
      ? (paletteEntry as { main: string }).main
      : theme.palette.primary.main;

  const primaryColor = primaryColorProp ?? palettePrimaryColor;

  const isColoured = colour === "coloured";
  const isSeparate = displaySize === "separate";

  const resolveThemeValue = (
    value: ThemeValue | undefined,
    fallback: string
  ) => {
    if (typeof value === "function") return value(theme);
    return value ?? fallback;
  };

  const resolvedIconColor = isColoured
    ? resolveThemeValue(iconColor, theme.palette.getContrastText(primaryColor))
    : resolveThemeValue(iconColor, "inherit");

  const resolvedHoverIconColor = resolveThemeValue(
    hoverIconColor,
    resolvedIconColor
  );

  const resolvedHoverBackgroundColor = isColoured
    ? primaryColor
    : resolveThemeValue(hoverBackgroundColor, theme.palette.action.hover);

  return (
    <IconButton
      {...props}
      sx={{
        borderRadius: "50%",
        width: isSeparate ? 40 : 30,
        height: isSeparate ? 40 : 30,
        minWidth: isSeparate ? 40 : 30,
        backgroundColor: isColoured ? primaryColor : "transparent",
        color: resolvedIconColor,
        "&:hover": {
          backgroundColor: resolvedHoverBackgroundColor,
          color: resolvedHoverIconColor,
        },
        ...sx,
      }}
    >
      {icon}
    </IconButton>
  );
};

/* -------------------------------------------------------------------------- */
/*                            RectangularTextButton                           */
/* -------------------------------------------------------------------------- */

export type RectangularTextButtonColour = "highlighted" | "clear";

export interface RectangularTextButtonProps
  extends Omit<ButtonProps, "color" | "variant" | "size"> {
  /**
   * highlighted = filled coloured button
   * clear = transparent/outlined button
   */
  colour?: RectangularTextButtonColour;

  /**
   * MUI palette key, e.g. "primary", or custom route colour key.
   */
  colorKey?: string;

  /**
   * Direct colour override.
   * Useful when parent already calculated a route/theme colour.
   */
  primaryColor?: string;

  /**
   * Text colour override.
   */
  textColor?: string;

  /**
   * Border colour override for clear state.
   */
  borderColor?: string;

  /**
   * Button size.
   */
  buttonSize?: "small" | "medium" | "large";

  /**
   * Allows anchor use if needed.
   */
  component?: React.ElementType;
  href?: string;
  target?: string;
  rel?: string;
}

export const RectangularTextButton: React.FC<RectangularTextButtonProps> = ({
  colour = "highlighted",
  colorKey = "primary",
  primaryColor: primaryColorProp,
  textColor: textColorProp,
  borderColor: borderColorProp,
  buttonSize = "small",
  sx,
  children,
  ...props
}) => {
  const theme = useTheme();

  const paletteEntry = theme.palette[colorKey as keyof typeof theme.palette];

  const muiPrimaryColor =
    typeof paletteEntry === "object" && paletteEntry && "main" in paletteEntry
      ? (paletteEntry as { main: string }).main
      : theme.palette.primary.main;

  const customPrimaryColor =
    colorKey && (theme.custom as any)?.[colorKey]
      ? (theme.custom as any)[colorKey].primary.dark
      : undefined;

  const primaryColor =
    primaryColorProp ??
    customPrimaryColor ??
    muiPrimaryColor ??
    theme.custom.base.primary.dark;

  const isHighlighted = colour === "highlighted";

  const highlightedTextColor =
    textColorProp ?? theme.palette.getContrastText(primaryColor);

  const clearTextColor =
    textColorProp ??
    theme.custom?.base?.text?.primary?.dark ??
    theme.palette.text.primary;

  const clearBorderColor =
    borderColorProp ??
    primaryColor ??
    theme.custom?.base?.text?.primary?.dark ??
    theme.palette.divider;

  return (
    <Button
      {...props}
      size={buttonSize}
      variant={isHighlighted ? "contained" : "outlined"}
      sx={{
        borderRadius: 1.5,
        px: buttonSize === "small" ? 1.5 : 2,
        py: buttonSize === "small" ? 0.5 : 0.75,

        backgroundColor: isHighlighted ? primaryColor : "transparent",
        color: isHighlighted ? highlightedTextColor : clearTextColor,
        borderColor: isHighlighted ? primaryColor : clearBorderColor,

        "&:hover": {
          backgroundColor: isHighlighted ? primaryColor : "transparent",
          borderColor: isHighlighted ? primaryColor : clearBorderColor,
          color: isHighlighted ? highlightedTextColor : clearTextColor,
        },

        "&.Mui-disabled": {
          opacity: 0.5,
          color: isHighlighted ? highlightedTextColor : clearTextColor,
          backgroundColor: isHighlighted ? primaryColor : "transparent",
          borderColor: isHighlighted ? primaryColor : clearBorderColor,
        },

        ...sx,
      }}
    >
      {children}
    </Button>
  );
};


/* -------------------------------------------------------------------------- */
/*                              FancyTextButton                               */
/* -------------------------------------------------------------------------- */

export type FancyTextButtonVariant = "normal" | "gradient";

export interface FancyTextButtonProps
  extends Omit<ButtonProps, "variant" | "color"> {
  /**
   * normal = solid green button like Hero CTA
   * gradient = gradient button like Register/Login in public navbar
   */
  fancyVariant?: FancyTextButtonVariant;

  /**
   * Pill shape by default. Set to false for mobile menu-style buttons.
   */
  pill?: boolean;

  /**
   * Optional custom background for normal button.
   */
  background?: string;

  /**
   * Optional custom hover background for normal button.
   */
  hoverBackground?: string;

  /**
   * Optional custom gradient.
   */
  gradient?: string;

  /**
   * Optional custom hover gradient.
   */
  hoverGradient?: string;

  /**
   * Text colour.
   */
  textColor?: string;
}

export const FancyTextButton: React.FC<FancyTextButtonProps> = ({
  fancyVariant = "normal",
  pill = true,
  background = "#4ade80",
  hoverBackground = "#22c55e",
  gradient = "linear-gradient(135deg,#4ade80 0%,#2dd4bf 100%)",
  hoverGradient = "linear-gradient(135deg,#34d399 0%,#22d3ee 100%)",
  textColor = "#0f172a",
  sx,
  children,
  ...props
}) => {
  const isGradient = fancyVariant === "gradient";

  return (
    <Button
      {...props}
      variant="contained"
      sx={{
        background: isGradient ? gradient : background,
        color: textColor,
        borderRadius: pill ? "9999px" : "12px",
        px: 3,
        textTransform: "none",
        fontWeight: isGradient ? 600 : 900,
        boxShadow: isGradient
          ? undefined
          : "0px 10px 30px rgba(0,0,0,0.35)",

        "&:hover": {
          background: isGradient ? hoverGradient : hoverBackground,
        },

        ...sx,
      }}
    >
      {children}
    </Button>
  );
};